import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FaxTemplateModel} from '../../api/models/fax-template-model';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {FieldDescription} from '../../editors/dynamic-form/field-description';
import {DateTime} from 'luxon';
import {DynamicFormComponent} from '../../editors/dynamic-form/dynamic-form.component';
import {debounceTime} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import { NgIf } from '@angular/common';
import { PdfViewerComponent } from '../../editors/pdf-viewer/pdf-viewer.component';


@Component({
    selector: 'app-fax-template',
    template: `
    <div class="row">
      <div class="upload-wrapper col-lg-6">
        <app-dynamic-form #dynamicForm [formModel]="formModel"
                          [formModelDescription]="formModelDescription"
                          (formChanged)="onFormChanged($event)">
        </app-dynamic-form>
      </div>
      <div class="preview-attachment col-lg-6" *ngIf="previewFileName">
        <div #previewContainer class="inner-container" [style.height]="containerHeight + 'px'">
          <div style="display: inline-block; width: 100%; padding: 5px; background-color: gray; height: 100%">
            <app-pdf-viewer [src]="previewPdf" *ngIf="previewPdf"
                        (afterLoadComplete)="afterPdfLoaded()"
                        class="preview-holder"
            ></app-pdf-viewer>
          </div>
        </div>
      </div>
    </div>`,
    styleUrls: ['./fax-template.component.scss'],
    standalone: true,
    imports: [
        DynamicFormComponent,
        NgIf,
        PdfViewerComponent,
    ],
})
export class FaxTemplateComponent implements OnInit, OnChanges, OnDestroy {

  @Input() model: FaxTemplateModel;
  @Input() parentData: any;

  file: File | Blob;
  previewPdf: string;
  previewFileName: string;

  formModel: any = {};
  formModelDescription: FieldDescription[] = [];

  checkedImage: string;
  unCheckedImage: string;
  containerHeight = 100;
  resizeSubscription: Subscription;
  formChangeSubscription: Subscription;

  pdfPageLoadTimeout: any;

  @Output() valueChanged = new EventEmitter<File | Blob>();
  @Output() validationError = new EventEmitter<string>();

  fileRead = new EventEmitter<string>();
  @ViewChild('previewContainer') previewContainer: ElementRef;
  @ViewChild('dynamicForm', {static: true}) dynamicForm: DynamicFormComponent;

  constructor() {
    this.checkedImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAYAAABWzo5XAAAA10lEQVQ4T63UMWoCURAG4E8QchG9g12CCFpIGpMbpJDcIYh4iKQW7AyIpEmnEELOELxIwCIMuPBQ13WXfd3C8DH/ezPbUNNp1ORIoTZe0bwS3+MZv1GfQkuMrkSysnc8HEMb3JaEtrirCq0xxwI/VaFAIn4Pq6rQJ4YJcoPcaB084gn3yX0FEt/dQyeBxLkIDfCFD/SRh1yE4tX+ENg3ppic6SRrtvDVMixGItC42CxOOiGFUBQHNsNLDlIYrcxMnu2othVp4a3k0o6xO16RMpFOamv7H/0DL8Y0E8CGBbkAAAAASUVORK5CYII='; // await this.getBase64Image('/assets/icons/baseline_check_box_black_18dp.png');
    this.unCheckedImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAYAAABWzo5XAAAAdklEQVQ4T2NkoBJgpJI5DMgGqTIwMExnYGBgIdLwPwwMDFkMDAy3QOqRDVrDwMDgwsDAcIFIgwwYGBj2MDAwhKAbdABqgAORBqGoR3bRqEHwEBwNI8KJifZhRLUsosLAwDCDxEybwcDAcAc9rxEOFTwqqFYeAQDicCAToKXz/wAAAABJRU5ErkJggg=='; // await this.getBase64Image('/assets/icons/baseline_check_box_outline_blank_black_18dp.png');

    //(pdfMake as any).vfs = pdfFonts;
    //(pdfMake as any)['vfs'] = pdfFonts;
    (pdfMake as any).addVirtualFileSystem(pdfFonts);
  }

  removeAttachment() {
    this.file = undefined;
    this.previewFileName = undefined;

    this.valueChanged.emit(this.file);
  }

  ngOnInit(): void {
    console.log('after ngOnInit');
    this.fileRead.subscribe((data) => {
      console.log('on file is ready to preview');
      this.previewPdf = data;
    });

    this.formChangeSubscription = this.dynamicForm.formChanged
      .pipe(debounceTime(300))
      .subscribe((val) => {
        console.log('formChanged');
        this.displayPdf();
      });
  }

  displayPdf() {
    console.log('display pdf');

    const pdfDocGenerator = pdfMake.createPdf(this.getPdfContent());

    pdfDocGenerator.getBlob(async (blob) => {
      this.file = blob;
      this.valueChanged.emit(this.file);
      this.previewFile(blob);
    });

  }

  getPdfContent() {
    console.log('get pdf content');
    if (this.model.pdfTemplate) {
      let pdfContentString = atob(this.model.pdfTemplate);
      const allData = Object.assign({}, this.formModel, this.parentData);

      for (const key of Object.keys(allData)) {
        const pattern = new RegExp(`{{${key}}}`, 'g');
        const rawValue = allData[key];
        const value = rawValue instanceof Date
          ? DateTime.fromJSDate(rawValue).toLocaleString()
          : typeof rawValue === typeof true ? rawValue ? this.checkedImage : this.unCheckedImage
            : rawValue;

        pdfContentString = pdfContentString.replace(pattern, value);
      }

      pdfContentString = pdfContentString.replace(/[\n\r]/g, '\\n');
      const pdfContent = JSON.parse(pdfContentString);

      return pdfContent;
    }

    return {};
  }

  getFormModel() {
    console.log('get form fields');
    if (this.model.formFields) {
      const formFieldsString = atob(this.model.formFields);

      this.formModelDescription = JSON.parse(formFieldsString);
    }

    this.formModel = {};

    for (const fieldDescription of this.formModelDescription) {
      if (fieldDescription.fieldType === 'date' && fieldDescription.defaultValue === 'today') {
        this.formModel[fieldDescription.fieldName] = new Date();
      } else {
        this.formModel[fieldDescription.fieldName] = fieldDescription.defaultValue;
      }
    }
  }

  onFormChanged($event: any) {
    console.log('onFormChanged', $event);
    // this.formModel = $event;
    // this.displayPdf();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes);
    const {parentData: parentData1, model: model1} = changes;
    if (model1) {
      this.getFormModel();
      this.displayPdf();
    }
    if (parentData1) {
      this.displayPdf();
    }
  }

  updateSize() {
    if (this.previewContainer) {
      this.containerHeight = this.previewContainer.nativeElement.clientWidth * 1.4142 + 10;
    }
  }

  private previewFile(blob: Blob) {
    this.previewFileName = 'cover-page.pdf';
    console.log('previewFile');

    if (blob.type.indexOf('pdf') > -1) {
      console.log('read pdf file');
      const reader = new FileReader();

      reader.onload = (e: any) => {
        console.log('pdf file loaded');
        clearTimeout(this.pdfPageLoadTimeout);
        this.pdfPageLoadTimeout = setTimeout(() => {
          this.fileRead.emit(e.target.result);
          this.updateSize();
        }, 300);
      };
      reader.readAsArrayBuffer(blob);
    }
  }

  afterPdfLoaded() {
    window.dispatchEvent(new Event('resize'));
  }

  ngOnDestroy(): void {
    this.resizeSubscription?.unsubscribe();
    this.formChangeSubscription?.unsubscribe();
  }
}
